import { Component } from "./base/Component";
import { ComponentFactory } from "../service/ComponentFactory";
import { EventUtil } from "../util/EventUtil";
import { HealioHeader } from "./healioGlobal/HealioHeader";
import { ISavedPageAddRequest } from "../models/SavedPage/ISavedPageAddRequest";
import { LocalStorageUtil } from "../util/LocalStorageUtil";
import { PageContext } from "../service/PageContext";
import { WyanokeUtil } from "../util/WyanokeUtil";
import { SavedPageService } from "../service/SavedPageService";
import { mapRowToGetResponse, ParsedSavedMeta } from "../service/HealioWidgetsSavedApi";

export class SaveIcon extends Component {
  private readonly dataStore = "SaveIcon";
  private readonly dataStoreKey = "AttemptedToSaveItem";
  private saveIconStorage : LocalStorageUtil | null = null;
  private readonly _savedPageService: SavedPageService | null;

  constructor(element: HTMLElement, pageContext: PageContext) {
    super(element, pageContext);

    element.addEventListener("click", this.handleSaveHandler);

    this._savedPageService = pageContext ? pageContext.savedPageService : null;
    if (this._savedPageService) {

      window.addEventListener(
        SavedPageService.SavedPageDecrementEvent,
        ev => this.handleSaveRemoved(ev as CustomEvent<string>));
    }
  }

  public async init(): Promise<void> {
    this.saveIconStorage = new LocalStorageUtil(this.dataStore);
    await this.updateCurrentState();
    await this.saveAfterLogIn();
  }

  private async handleSaveRemoved(ev: CustomEvent<string>): Promise<void> {
    if(ev.detail && ev.detail === this._element.dataset.activityId && this.state !== "save"){
      this.state = "save"
    }
    return
  }

  private readonly handleSaveHandler = (ev: MouseEvent) => this.handleSave(ev);

  private get label(): HTMLElement | null {
    const selector = "[data-save-icon='label']";

    return this._element.querySelector(selector) as HTMLElement;
  }

  private get icon(): HTMLElement | null {
    const selector = "[data-save-icon='icon']";

    return this._element.querySelector(selector) as HTMLElement;
  }

  public get state(): "save" | "saved" | "complete" | null {
    const current = this._element.getAttribute("data-state-current");

    this._element.classList.add("complete");

    return current === "save" || current === "saved" || current === "complete"
      ? current
      : null;
  }

  public set state(value: "save" | "saved" | "complete" | null) {
    if (value) {
      this._element.setAttribute("data-state-current", value);
    } else {
      this._element.removeAttribute("data-state-current");
    }

    if (value && value === "complete") {
      this._element.removeEventListener("click", this.handleSaveHandler);
      this._element.classList.add("completed");
    }

    const label = this.label;
    if (label) {
      const text = value ? label.getAttribute("data-label-" + value) || "" : "";
      label.textContent = text;
      label.classList.remove("d-none");
    }

    const icon = this.icon;
    if (icon) {
      if (value === "saved") {
        icon.classList.add("fas");
        icon.classList.remove("fal");
      } else if (value === "complete") {
        icon.className = "";
        icon.className = "fas fa-check-circle";
      } else {
        icon.classList.add("fal");
        icon.classList.remove("fas");
      }
      icon.parentElement?.classList.remove("d-none");
    }

    if(this._element.classList.contains("pin")){
      if(value === "saved"){
        this._element.classList.add("active");
      } else {
        this._element.classList.remove("active");
      }
    }
  }

  private async handleSave(ev: MouseEvent): Promise<void> {
    if(ev && ev !== null) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const method = this.state !== "saved" ? "save" : "remove";
    const context = this._pageContext;
    const service = context ? context.savedPageService : null;
    const saveActivityPage = await this.savedPage();
    const secondaryContentType = this._element.dataset.secondaryContentType;
    const shouldSaveSecondaryContentType =
      !!secondaryContentType &&
      secondaryContentType !== this._element.dataset.contentType;
    const secondarySaveActivityPage = shouldSaveSecondaryContentType
      ? await this.savedPage(secondaryContentType)
      : null;

    if (saveActivityPage !== null && service) {
      const saveMeta = this.readSaveMetaFromDataset();
      if (method === "save") {
        // One row per item. data-secondary-content-type (e.g. "news" on a
        // podcast episode) used to save the item a second time under another
        // type for the old listing. The saved-items API now keys on contact +
        // item id alone, so a second save would only update the same row. Only
        // the primary type is saved. Removing below also removes under the
        // secondary type, which is a harmless no-op on the API now.
        service.save(saveActivityPage, saveMeta);
      } else {
        service.remove(saveActivityPage);
        if (secondarySaveActivityPage !== null) {
          service.remove(secondarySaveActivityPage);
        }
      }
      this.state = method === "save" ? "saved" : "save";
      if (method === "save") {
        const event = new CustomEvent("SaveIconSaved", { detail: saveActivityPage });
        window.dispatchEvent(event);
      } else if (method === "remove") {
        const event = new CustomEvent("SaveIconRemoved", { detail: saveActivityPage });
        window.dispatchEvent(event);
      }
    } else {
      const error =
        saveActivityPage === null
          ? "[testing SAP && service] Unable to create Save Page."
          : "[testing SAP && service] Could not resolve Saved Page Service";

      console.error(error);
    }
  }

  private async saveAfterLogIn(): Promise<void> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      return;
    }
    const storedSaveIcon = (this.saveIconStorage?.getItem(this.dataStore, [
      this.dataStoreKey
    ])) as { activityId: string };
    if (
      !storedSaveIcon ||
      storedSaveIcon.activityId !== this._element.dataset.activityId
    ) {
      return;
    }
    this.saveIconStorage?.deleteItem(this.dataStore, [this.dataStoreKey]);
    if (this.state === "saved") {
      return;
    }
    this._element.dispatchEvent(EventUtil.createCustomEvent("click", null));
  }

  /**
   * Card fields stored with the saved row so My Saved can render it without
   * another lookup. Explicit data-* attributes win; anything missing is read
   * from the card the icon sits in, or from the page's own meta tags when the
   * icon is on the article page itself.
   */
  private readSaveMetaFromDataset(): Partial<ParsedSavedMeta> {
    const ds = this._element.dataset;
    const explicitImage = ds.activityImageUrl || ds.imageUrl;
    // Only scrape the card when the view didn't render the fields (SaveIconCardData).
    const scraped: Partial<ParsedSavedMeta> =
      explicitImage && (ds.activityTeaser || ds.activityPostedDate)
        ? {}
        : this.readSaveMetaFromPage();
    return {
      teaserHtml: ds.activityTeaser || ds.teaserHtml || scraped.teaserHtml,
      // Search-result templates render root-relative media URLs; the saved row is shown on another host.
      imageUrl: explicitImage ? SaveIcon.absoluteUrl(explicitImage) : scraped.imageUrl,
      postedDate: ds.activityPostedDate || ds.postedDate || scraped.postedDate,
      engagementText:
        ds.activityEngagement || ds.engagementText || scraped.engagementText,
      cardKind: ds.cardKind || scraped.cardKind,
      // CME activity cards render data-credits next to data-expires (the latter is
      // sent as the row's `expires`); My CME's Saved tab shows both.
      credits: ds.credits || undefined,
    };
  }

  private readSaveMetaFromPage(): Partial<ParsedSavedMeta> {
    const card = this.findCardElement();
    const meta: Partial<ParsedSavedMeta> = {};

    if (card) {
      const img = card.querySelector("img");
      const src = img
        ? img.getAttribute("src") || img.getAttribute("data-src")
        : null;
      if (src) {
        meta.imageUrl = SaveIcon.absoluteUrl(src);
      }

      const dateEl = card.querySelector(
        ".date, [class*='__date'], time, .article__posted-date"
      );
      const dateText = dateEl && dateEl.textContent ? dateEl.textContent.trim() : "";
      if (dateText) {
        meta.postedDate = dateText;
      }

      const teaserEl = card.querySelector(
        ".card-text, [class*='__teaser'], [class*='__description'], p"
      );
      const teaserText =
        teaserEl && teaserEl.textContent ? teaserEl.textContent.trim() : "";
      if (teaserText) {
        meta.teaserHtml = SaveIcon.escapeHtml(teaserText);
      }

      const durationEl = card.querySelector(
        ".time, [class*='__duration'], [class*='read-time']"
      );
      const durationText =
        durationEl && durationEl.textContent ? durationEl.textContent.trim() : "";
      if (durationText) {
        const isPodcast = /podcast/i.test(this._element.dataset.contentType || "");
        meta.engagementText =
          isPodcast && !/listen/i.test(durationText)
            ? durationText + " listen"
            : durationText;
      }
    } else if (
      this._element.dataset.activityUrl &&
      this._element.dataset.activityUrl === window.location.pathname
    ) {
      // The icon is on the item's own page: use its social meta tags.
      const og = (property: string): string | undefined => {
        const tag = document.querySelector('meta[property="' + property + '"]');
        const value = tag ? tag.getAttribute("content") : null;
        return value ? value.trim() : undefined;
      };
      const image = og("og:image");
      if (image) {
        meta.imageUrl = SaveIcon.absoluteUrl(image);
      }
      const description = og("og:description");
      if (description) {
        meta.teaserHtml = SaveIcon.escapeHtml(description);
      }
      const published = og("article:published_time");
      if (published) {
        meta.postedDate = published;
      }
    }

    return meta;
  }

  /**
   * The nearest ancestor that holds an image and only this one save icon,
   * i.e. this item's card (never a list container or the whole page).
   */
  private findCardElement(): HTMLElement | null {
    let node: HTMLElement | null = this._element.parentElement;
    while (node && node !== document.body) {
      if (node.querySelectorAll("[data-component='SaveIcon']").length > 1) {
        return null;
      }
      if (node.querySelector("img")) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  private static absoluteUrl(value: string): string {
    try {
      return new URL(value, window.location.href).toString();
    } catch {
      return value;
    }
  }

  private static escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  private async savedPage(
    contentTypeOverride?: string
  ): Promise<ISavedPageAddRequest | null> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);

    if (!profile) {
      // tslint:disable-next-line: no-unsafe-any
      this.saveIconStorage?.insertItem(
        this.dataStore,
        [this.dataStoreKey],
        this._element.dataset
      );

      this._element.dispatchEvent(
        EventUtil.createCustomEvent(
          HealioHeader.UnauthenticatedActionEvent,
          null
        )
      );

      return null;
    }

    const userId = profile.contactid || null;
    const activityId = this._element.dataset.activityId || null;
    const activityUrl = this._element.dataset.activityUrl || null;
    const activityTitle = this._element.dataset.activityTitle || null;
    const contentType =
      contentTypeOverride || this._element.dataset.contentType || "cme";
    const siteName = this._element.dataset.siteName || "cme-healio";
    const expiresDate = this._element.dataset.expires || null;
    if (!userId || !activityId || !activityUrl || !activityTitle) {
      return null;
    }

    return {
      contenttype: contentType.split(","),
      documentid: activityId,
      expires: expiresDate,
      site: siteName,
      title: activityTitle,
      url: activityUrl,
      userid: userId
    };
  }

  private async updateCurrentState(): Promise<void> {
    const context = this._pageContext;
    const service = context ? context.savedPageService : null;

    let contentType = "cme";

    if (this._element.dataset.activityId === undefined) {
      console.warn("No Item Id found on Card");

      return;
    }

    if (this._element.dataset.siteName === undefined) {
      console.warn("No siteName Card");

      return;
    }

    if (this._element.dataset.contentType !== undefined) {
      const ct = this._element.dataset.contentType;
      contentType = ct !== null && ct !== "" ? ct : contentType;
    }

    if (!service) {
      console.warn("No Saved Page Service available.");

      return;
    }

    // An item is one saved row whatever type it was saved under (news / podcast
    // episode, CME as `cme` or `activity`), so the state is looked up by item id
    // across every row. Filtering by this icon's content type would show an item
    // as unsaved when it was saved under another type name. The all-rows fetch is
    // shared and cached across the icons on the page.
    let savedPages: ReadonlyArray<{ documentid: string; site: string }> | null;
    try {
      savedPages = (await service.listAllRows()).map(mapRowToGetResponse);
    } catch (error) {
      console.warn("[SaveIcon] saved list failed", error);
      savedPages = [];
    }

    const activityId = this._element.dataset.activityId.toLowerCase();
    const activitySiteName = this._element.dataset.siteName.toLowerCase();
    const isSaved =
      savedPages === null || savedPages.length < 1
        ? false
        : savedPages.some(
            // Rows saved by the Next apps (Vindico, Healio) carry no site in
            // their data, so a row without a site matches on the item id alone.
            s => s.documentid && s.documentid.toLowerCase() === activityId && (!s.site || s.site.toLowerCase() === activitySiteName)
          );

    let iconState: "save" | "saved" | "complete" | null = isSaved
      ? "saved"
      : "save";
    // we have an additional state when this is a cme activity
    if (contentType === "cme") {
      const profile = await WyanokeUtil.loadUserProfile(true, true);
      if (profile && profile.completedactivities) {
        iconState = profile.completedactivities.some(
          act => act.toLowerCase() === activityId
        )
          ? "complete"
          : iconState;
      }
    }

    this.state = iconState;
  }
}

ComponentFactory.registerComponent("SaveIcon", SaveIcon);
